/**
 * Audit UI — 통합 검색 hook + input mode detector.
 *
 * useUnifiedSearch: 5 API 병렬 호출 + 결과 합성
 * detectMode: input → "keyword" | "line_item"
 */
import { useEffect, useState, useCallback } from "react";

const TIMEOUT_MS = 60000;

export function detectMode(input) {
  const trimmed = (input || "").trim();
  if (!trimmed) return "empty";
  const words = trimmed.split(/\s+/).filter(Boolean).length;
  const hasDigits = /\d/.test(trimmed);
  const isShort = trimmed.length < 15 && words <= 2;
  if (isShort && !hasDigits) return "keyword";
  return "line_item";
}

async function fetchJson(url, body) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    if (!r.ok) {
      let err = `HTTP ${r.status}`;
      try {
        err = (await r.json())?.error || err;
      } catch (_) {}
      throw new Error(err);
    }
    return await r.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 통합 검색 훅.
 * query: 사용자 입력 (string)
 * options.chapter: 명시 chapter (optional)
 *
 * returns: { mode, ai, tariff, precedents, notes, laws, loading, errors, refetch }
 */
export function useUnifiedSearch(query, options = {}) {
  const { chapter = null } = options;
  const mode = detectMode(query);

  const [state, setState] = useState({
    mode,
    ai: null,
    tariff: null,
    precedents: null,
    notes: null,
    laws: null,
    loading: {
      ai: false,
      tariff: false,
      precedents: false,
      notes: false,
      laws: false,
    },
    errors: {},
  });

  const run = useCallback(async (q, ch) => {
    const m = detectMode(q);
    if (m === "empty") {
      setState((s) => ({
        ...s,
        mode: m,
        ai: null,
        tariff: null,
        precedents: null,
        notes: null,
        laws: null,
      }));
      return;
    }

    setState((s) => ({
      ...s,
      mode: m,
      loading: {
        ai: m === "line_item",
        tariff: true,
        precedents: true,
        notes: true,
        laws: false,
      },
      errors: {},
    }));

    const aiPromise =
      m === "line_item"
        ? fetchJson("/api/hs-classify-v2", {
            line_item: q,
            chapter: ch || "auto",
            audit: true,
          })
            .then((data) => ({ key: "ai", data }))
            .catch((err) => ({ key: "ai", error: String(err) }))
        : Promise.resolve({ key: "ai", data: null });

    const tariffPromise = fetchJson("/api/hs-tariff-search", {
      keyword: q,
      chapter: ch,
      page_size: 30,
    })
      .then((data) => ({ key: "tariff", data }))
      .catch((err) => ({ key: "tariff", error: String(err) }));

    const precPromise = fetchJson("/api/precedents-search", {
      keyword: q,
      chapter: ch,
      page_size: 2000,
    })
      .then((data) => ({ key: "precedents", data }))
      .catch((err) => ({ key: "precedents", error: String(err) }));

    const notesPromise = fetchJson("/api/hs-notes-search", {
      keyword: q,
      chapter: ch,
    })
      .then((data) => ({ key: "notes", data }))
      .catch((err) => ({ key: "notes", error: String(err) }));

    const promises = [aiPromise, tariffPromise, precPromise, notesPromise];
    promises.forEach((p) => {
      p.then(({ key, data, error }) => {
        setState((s) => {
          const next = { ...s };
          next.loading = { ...s.loading, [key]: false };
          if (error) {
            next.errors = { ...s.errors, [key]: error };
          } else {
            next[key] = data;
          }
          return next;
        });

        // AI 결정 완료 시 laws GET 호출 (cascading)
        if (key === "ai" && data?.hs_4digit) {
          setState((s) => ({ ...s, loading: { ...s.loading, laws: true } }));
          fetch(`/api/laws/heading/${data.hs_4digit}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
            .then((lawsData) => {
              setState((s) => ({
                ...s,
                laws: lawsData,
                loading: { ...s.loading, laws: false },
              }));
            });
        }
      });
    });
  }, []);

  useEffect(() => {
    if (query) run(query, chapter);
  }, [query, chapter, run]);

  return { ...state, refetch: () => run(query, chapter) };
}

// PDF 다운로드 헬퍼
export async function downloadAuditPdf({ line_item, chapter }) {
  const r = await fetch("/api/hs-classify-v2/pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      line_item,
      chapter: chapter || "auto",
      audit: true,
    }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err?.detail || `HTTP ${r.status}`);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hs-audit-${Date.now()}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
